/**
 * ZoneDisplayPage — browser-based PA device for a zone (terminal / bus / etc.)
 * Open at /zone-display/:zoneCode  e.g. /zone-display/T1  or /zone-display/BUS_T1_T3
 *
 * Key fixes vs original:
 *  - isPlayingRef guards playFromQueue() so a new incoming command never interrupts
 *    audio that is already playing — it just gets queued
 *  - repeatStateRef is initialized in playFromQueue() (per-item), NOT in the
 *    zone:command handler, so a second command arriving mid-play can't overwrite the
 *    current item's repeat counter
 *  - nowPlayingRef mirrors nowPlaying state so onEnded (a stale closure) can still
 *    read the correct value when emitting zone:played
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { io } from 'socket.io-client';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || '';

export default function ZoneDisplayPage() {
  const { zoneCode } = useParams();

  const [connected, setConnected] = useState(false);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [playerStatus, setPlayerStatus] = useState('idle');
  const [nowPlaying, setNowPlaying] = useState(null);
  const [log, setLog] = useState([]);

  const socketRef        = useRef(null);
  const audioRef         = useRef(new Audio());
  const queueRef         = useRef([]);
  const audioUnlockedRef = useRef(false);
  const isPlayingRef     = useRef(false);   // guard: true while audio.play() is active
  const nowPlayingRef    = useRef(null);    // mirrors nowPlaying to avoid stale closure
  const suppressEndedRef = useRef(false);
  const suppressErrorRef = useRef(false);
  const repeatStateRef   = useRef(null);   // { payload, remaining, intervalSec } — set per item in playFromQueue

  const addLog = useCallback((msg) => {
    const t = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    setLog(prev => [`[${t}] ${msg}`, ...prev].slice(0, 30));
  }, []);

  // ── Audio element event handlers ───────────────────────────────────────────
  useEffect(() => {
    const audio = audioRef.current;

    const onEnded = () => {
      if (suppressEndedRef.current) {
        suppressEndedRef.current = false;
        isPlayingRef.current = false;
        return;
      }

      isPlayingRef.current = false;
      const current = nowPlayingRef.current;   // read ref, not stale state
      setPlayerStatus('idle');
      setNowPlaying(null);
      nowPlayingRef.current = null;

      const rs = repeatStateRef.current;
      if (rs && rs.remaining > 1) {
        // Decrement remaining and re-queue the SAME item, carrying the already-decremented
        // count on the payload (_remainingOverride) — playFromQueue() must honour this
        // rather than re-reading payload.repeatCount, or every repeat resets the counter
        // back to the original value and the announcement repeats forever.
        const newRemaining = rs.remaining - 1;
        repeatStateRef.current = { ...rs, remaining: newRemaining };
        addLog(`Repeat in ${rs.intervalSec}s (${newRemaining - 1} left)…`);
        const repeatPayload = { ...rs.payload, _remainingOverride: newRemaining };
        setTimeout(() => {
          queueRef.current.unshift(repeatPayload);
          playFromQueue();
        }, rs.intervalSec * 1000);
      } else {
        repeatStateRef.current = null;
        // Report back to supervisor room
        if (socketRef.current && current) {
          socketRef.current.emit('zone:played', { announcementId: current.announcementId, zoneCode });
        }
        setTimeout(() => playFromQueue(), 800);
      }
    };

    const onError = () => {
      if (suppressErrorRef.current) {
        suppressErrorRef.current = false;
        isPlayingRef.current = false;
        return;
      }
      addLog('Audio error — skipping');
      isPlayingRef.current = false;
      setPlayerStatus('idle');
      setNowPlaying(null);
      nowPlayingRef.current = null;
      repeatStateRef.current = null;
      setTimeout(() => playFromQueue(), 1000);
    };

    audio.addEventListener('ended', onEnded);
    audio.addEventListener('error', onError);
    return () => {
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('error', onError);
    };
  }, [zoneCode, addLog]);

  // ── Socket setup ───────────────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { addLog('Not authenticated'); return; }

    const socket = io(SOCKET_URL, { auth: { token }, transports: ['websocket'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      addLog(`Connected — registering as zone ${zoneCode}`);
      socket.emit('zone:register', zoneCode);
    });

    socket.on('disconnect', () => { setConnected(false); addLog('Disconnected'); });

    socket.on('zone:command', ({ command, payload }) => {
      addLog(`Command: ${command}`);

      if (command === 'PLAY') {
        const { audioUrl } = payload;
        if (!audioUrl) { addLog('No audio URL — skipping'); return; }

        // Push to queue — do NOT set repeatStateRef here.
        // repeatStateRef is set per-item inside playFromQueue() when it actually starts.
        queueRef.current.push(payload);

        if (!audioUnlockedRef.current) {
          addLog('Audio locked — queued for after unlock');
          return;
        }
        // Only start playing if nothing is currently playing
        if (!isPlayingRef.current) playFromQueue();
        // else: onEnded will drain the queue when current item finishes
      }

      if (command === 'STOP') {
        suppressEndedRef.current = true;
        suppressErrorRef.current = true;
        audioRef.current.pause();
        audioRef.current.src = '';
        queueRef.current = [];
        repeatStateRef.current = null;
        isPlayingRef.current = false;
        setPlayerStatus('idle');
        setNowPlaying(null);
        nowPlayingRef.current = null;
        addLog('Stopped by command');
      }
    });

    return () => { socket.disconnect(); };
  }, [zoneCode, addLog]);

  // ── Audio queue player ─────────────────────────────────────────────────────
  function playFromQueue() {
    // Hard guard: never interrupt audio that is already playing
    if (isPlayingRef.current) return;
    if (queueRef.current.length === 0) return;

    const payload = queueRef.current.shift();
    const { audioUrl, englishText, announcementId, repeatCount = 1, repeatIntervalSec = 0 } = payload;

    // Set repeat state HERE — owned by this specific item, safe from overwrites.
    // A repeat continuation carries _remainingOverride (see onEnded) — honour it instead
    // of always resetting to the original repeatCount, or repeats never terminate.
    const remaining = payload._remainingOverride ?? repeatCount;
    repeatStateRef.current = { payload, remaining, intervalSec: repeatIntervalSec };
    isPlayingRef.current = true;

    const audio = audioRef.current;
    audio.src = audioUrl;
    audio.play()
      .then(() => {
        setPlayerStatus('playing');
        const np = { announcementId, text: englishText };
        setNowPlaying(np);
        nowPlayingRef.current = np;
        addLog(`Playing: ${englishText?.substring(0, 60)}…`);
      })
      .catch(err => {
        isPlayingRef.current = false;
        repeatStateRef.current = null;
        setNowPlaying(null);
        nowPlayingRef.current = null;
        addLog(`Play failed: ${err.message}`);
        setPlayerStatus('idle');
        setTimeout(() => playFromQueue(), 500);
      });
  }

  // ── Tap to unlock audio ────────────────────────────────────────────────────
  function unlockAudio() {
    const audio = audioRef.current;
    suppressEndedRef.current = true;
    audio.src = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';
    audio.play()
      .then(() => {
        audio.pause();
        audioUnlockedRef.current = true;
        setAudioUnlocked(true);
        addLog('Audio unlocked');
        if (queueRef.current.length > 0 && !isPlayingRef.current) playFromQueue();
      })
      .catch(() => {
        suppressEndedRef.current = false;
        addLog('Unlock failed — try again');
      });
  }

  // ── Zone display label ─────────────────────────────────────────────────────
  const zoneLabel = zoneCode === 'ALL' ? 'Airport Wide'
    : zoneCode?.startsWith('BUS_') ? `Bus ${zoneCode.replace('BUS_', '').replace(/_/g, ' ↔ ')}`
    : zoneCode || 'Zone';

  const zoneIcon = zoneCode?.startsWith('BUS_') ? '🚌' : zoneCode === 'ALL' ? '🏢' : '📡';

  return (
    <div className="min-h-screen bg-gray-950 text-white flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
        <div>
          <div className="text-2xl font-black tracking-tight">
            {zoneIcon} {zoneLabel}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">Zone PA Display · {zoneCode}</div>
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${connected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
          <span className="text-sm text-gray-400">{connected ? 'Online' : 'Offline'}</span>
        </div>
      </div>

      {/* Main area */}
      <div className="flex-1 flex flex-col items-center justify-center p-8">
        {!audioUnlocked ? (
          <button onClick={unlockAudio}
            className="px-12 py-6 rounded-2xl bg-blue-700 hover:bg-blue-600 text-white text-xl font-bold transition-all active:scale-95 shadow-lg">
            📢 Tap to Activate Audio
          </button>
        ) : playerStatus === 'playing' && nowPlaying ? (
          <div className="text-center max-w-lg">
            <div className="w-16 h-16 rounded-full bg-green-900 flex items-center justify-center mx-auto mb-5 animate-pulse">
              <span className="text-3xl">📣</span>
            </div>
            <div className="text-green-400 font-bold text-sm uppercase tracking-widest mb-3">Broadcasting</div>
            <p className="text-white text-lg leading-relaxed">{nowPlaying.text}</p>
            {repeatStateRef.current && repeatStateRef.current.remaining > 1 && (
              <p className="text-gray-500 text-xs mt-3">
                {repeatStateRef.current.remaining - 1} repeat{repeatStateRef.current.remaining - 1 > 1 ? 's' : ''} remaining
              </p>
            )}
          </div>
        ) : (
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">{queueRef.current.length > 0 ? '⏳' : '✅'}</span>
            </div>
            <p className="text-gray-400 font-medium">
              {queueRef.current.length > 0 ? `${queueRef.current.length} in queue…` : 'Standby — Audio Ready'}
            </p>
            <p className="text-gray-600 text-sm mt-1">Waiting for broadcast commands</p>
          </div>
        )}
      </div>

      {/* Activity log */}
      <div className="border-t border-gray-800 px-4 py-3 max-h-40 overflow-y-auto">
        <p className="text-xs text-gray-600 uppercase tracking-wide font-semibold mb-2">Activity Log</p>
        {log.length === 0 ? (
          <p className="text-xs text-gray-700">No activity yet</p>
        ) : log.map((entry, i) => (
          <p key={i} className="text-xs text-gray-500 font-mono leading-relaxed">{entry}</p>
        ))}
      </div>
    </div>
  );
}
